/**
 * Against Medical Advice (AMA) form — interactive form for populating
 * fields inline before generating the PDF.
 */
import { Input } from '@/frontend/components/ui/input'
import {
  AMA_LIABILITY_TEXT,
  AMA_UNDERSTANDING_TEXT,
  type AmaFormFields,
  AmaFormPdf,
  amaConsentText,
  amaRiskText,
  defaultAmaFormFields,
  formatAge,
  formatHormonalStatus,
} from '@/frontend/features/printing/documents/ama-form.print'
import { ClinicHeaderPreview } from '@/frontend/features/printing/documents/blocks/clinic-header'
import { DocumentPreview } from '@/frontend/features/printing/documents/blocks/document-preview'
import { OwnerDetailsPreview } from '@/frontend/features/printing/documents/blocks/owner-details'
import { OwnerSignaturePreview } from '@/frontend/features/printing/documents/blocks/owner-signature'
import { VetSignoffPreview } from '@/frontend/features/printing/documents/blocks/vet-signoff'
import type { DocumentFormProps } from '@/frontend/features/printing/documents/types'
import { withDocumentEditor } from '@/frontend/features/printing/documents/with-document-editor'

function AmaFormPreview({ context, value, onChange }: DocumentFormProps<AmaFormFields>) {
  const { patient, owner, clinic } = context
  const age = patient.dateOfBirth ? formatAge(patient.dateOfBirth) : ''

  return (
    <DocumentPreview>
      <ClinicHeaderPreview clinic={clinic} />

      <OwnerDetailsPreview owner={owner} />

      {/* Patient details table */}
      <div className="mt-4 border">
        <div className="border-b px-3 py-2 font-bold">{patient.name}</div>
        <table className="w-full">
          <tbody>
            <tr className="border-b">
              <td className="px-3 py-1.5 text-muted-foreground">Species</td>
              <td className="px-3 py-1.5">{patient.species}</td>
              <td className="px-3 py-1.5 text-muted-foreground">Visit Date</td>
              <td className="px-3 py-1.5">
                <Input
                  className="h-7 w-32"
                  value={value.visitDate}
                  onChange={(e) => onChange({ ...value, visitDate: e.target.value })}
                  placeholder="dd/mm/yyyy"
                />
              </td>
            </tr>
            <tr className="border-b">
              <td className="px-3 py-1.5 text-muted-foreground">Sex</td>
              <td className="px-3 py-1.5">{patient.sex}</td>
              <td className="px-3 py-1.5 text-muted-foreground">Weight</td>
              <td className="px-3 py-1.5">{context.weight || '—'}</td>
            </tr>
            <tr className="border-b">
              <td className="px-3 py-1.5 text-muted-foreground">Age</td>
              <td className="px-3 py-1.5">{age || '—'}</td>
              <td className="px-3 py-1.5 text-muted-foreground">Microchip Number</td>
              <td className="px-3 py-1.5">{patient.microchipNumber || '—'}</td>
            </tr>
            <tr className="border-b">
              <td className="px-3 py-1.5 text-muted-foreground">Breed</td>
              <td className="px-3 py-1.5">{patient.breed || '—'}</td>
              <td className="px-3 py-1.5 text-muted-foreground">Spayed/Neutered</td>
              <td className="px-3 py-1.5">{formatHormonalStatus(patient.isNeutered)}</td>
            </tr>
            <tr>
              <td className="px-3 py-1.5 text-muted-foreground">Colour</td>
              <td className="px-3 py-1.5">
                <Input
                  className="h-7 w-32"
                  value={value.colour}
                  onChange={(e) => onChange({ ...value, colour: e.target.value })}
                />
              </td>
              <td className="px-3 py-1.5 text-muted-foreground">Markings</td>
              <td className="px-3 py-1.5">
                <Input
                  className="h-7 w-32"
                  value={value.markings}
                  onChange={(e) => onChange({ ...value, markings: e.target.value })}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* AMA paragraphs */}
      <p>{amaConsentText(owner.firstName, owner.lastName)}</p>
      <p>{amaRiskText(patient.name)}</p>
      <p>{AMA_LIABILITY_TEXT}</p>
      <p>{AMA_UNDERSTANDING_TEXT}</p>

      <OwnerSignaturePreview owner={owner} label="Pet parent" />
      <VetSignoffPreview
        vetName={value.vetName}
        onChange={(v) => onChange({ ...value, vetName: v.vetName })}
        label="Licensed veterinarian"
        showSignature
        showDate
      />
    </DocumentPreview>
  )
}

export const amaForm = withDocumentEditor({
  id: 'ama-form',
  label: 'Against Medical Advice (AMA) Form',
  defaultFields: defaultAmaFormFields,
  Preview: AmaFormPreview,
  Pdf: AmaFormPdf,
})
